import "./relationshipView.scss";
import "./relationshipPopover.scss";

import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";
import { PopoverBody } from "reactstrap";

import { withContainerContext } from "../../components/container/ContainerContext";
// import SplitView from "../../components/splitView";
import FocusedPopover from "../../components/popover/FocusedPopover";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { withScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import withScrollableView from "../../components/scrollable/withScrollableView";
import { INVERSE_ID_PREFIX } from "../../constants/idPrefixes";
import { PropTypeShapes } from "../../types";
import { wrapEditingComponent } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { updateSelectedItemsUsingUpdaters } from "../../utils/selection";
import RelationshipCardCanvasView from "./RelationshipCardCanvasView";
import RelationshipColumnCanvasView from "./RelationshipColumnCanvasView";
import RelationshipPopoverList from "./RelationshipPopoverList";

// import debugUtils from "../../utils/debugUtils";

const referenceOptions = (
  allowedReferences,
  allowedInverseReferences,
  subjectTypeId,
  itemTypeId,
  currentReferences
) => {
  const list1 = allowedReferences.getIn([subjectTypeId, itemTypeId]).map(ref => {
    const isSelected = currentReferences
      ? currentReferences.findIndex(c => c.referenceId === ref.id && c.label === ref.label) >= 0
      : null;
    return {
      id: ref.id,
      label: ref.label,
      icon: ref.icon,
      color: "iconMono",
      selected: isSelected
    };
  });

  const list2 = allowedInverseReferences
    .getIn([subjectTypeId, itemTypeId])
    .map(ref => {
      const label = ref.inverseLabel ? ref.inverseLabel : ref.label;
      const isSelected = currentReferences
        ? currentReferences.findIndex(c => c.referenceId === ref.id && c.label === label) >= 0
        : null;
      return list1.find(r => r.id === ref.id)
        ? null
        : {
          id: INVERSE_ID_PREFIX + ref.id,
          label: label,
          icon: ref.icon,
          color: "iconMono",
          selected: isSelected
        };
    })
    .filter(ref => ref);

  return list1
    .concat(list2)
    .push({
      id: "none",
      label: "None",
      icon: "noIcon",
      color: "iconMono",
      selected: !currentReferences || currentReferences.length === 0
    })
    .toArray();
};

class RelationshipCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      scrollLeft: 0,
      scrollTop: 0,
      dropRow: null,
      dropAction: null,
      dimensions: {
        width: -1,
        height: -1
      },
      popoverOpen: false,
      popoverTargetId: null,
      popoverSubjectId: null,
      popoverItemId: null,
      popoverOptions: null
    };
    this.showMenu = this.showMenu.bind(this);
    this.hideMenu = this.hideMenu.bind(this);
    this.menuOptionClick = this.menuOptionClick.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onLiveRowDrag = this.onLiveRowDrag.bind(this);
    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onRowDrop = this.onRowDrop.bind(this);
    this.onClickPopoverBackdrop = this.onClickPopoverBackdrop.bind(this);
    this.selectRow = this.selectRow.bind(this);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("RelationshipCanvasView", this.props, nextProps, true);
    if (nextProps.sortedItemIds !== this.props.sortedItemIds) {
      this._lastClickItemId = null;
      this._firstShiftClickSelection = null;
    }
  }

  showMenu(subjectId, subjectType, itemId, itemType, currentReferences, targetElementId) {
    // Auto select the subject item to avoid confusing
    if (!preprocessorQuery.isIos()) {
      this.props.onSelectItems(subjectId);
    }

    const typeOptions = referenceOptions(
      this.props.allowedReferences,
      this.props.allowedInverseReferences,
      subjectType,
      itemType,
      currentReferences
    );

    this.setState({
      popoverOpen: true,
      popoverTargetId: targetElementId,
      popoverSubjectId: subjectId,
      popoverSubjectType: subjectType,
      popoverItemId: itemId,
      popoverItemType: itemType,
      popoverOptions: typeOptions
    });
  }

  selectRow(subjectId) {
    // Auto select the subject item to avoid confusing
    if (!preprocessorQuery.isIos()) {
      this.props.onSelectItems(subjectId);
    }
  }

  hideMenu() {
    this.setState({
      popoverOpen: false,
      popoverTargetId: null,
      popoverSubjectId: null,
      popoverSubjectType: null,
      popoverItemId: null,
      popoverItemType: null,
      popoverOptions: null
    });
  }

  menuOptionClick(incomingReferenceId, selected) {
    this.hideMenu();

    const isStraight = incomingReferenceId.indexOf(INVERSE_ID_PREFIX) === -1;
    const referenceId = isStraight ? incomingReferenceId : incomingReferenceId.substr(INVERSE_ID_PREFIX.length);
    const isNone = referenceId === "none";

    if (isNone) {
      this.props.onRelationshipChange(this.state.popoverSubjectId, referenceId, this.state.popoverItemId, selected);
    } else if (isStraight) {
      this.props.onRelationshipChange(this.state.popoverSubjectId, referenceId, this.state.popoverItemId, selected);
    } else {
      // As it is inverse, reverse subject & object so we delete the correct relationship
      this.props.onRelationshipChange(this.state.popoverItemId, referenceId, this.state.popoverSubjectId, selected);
    }
  }

  renderMenu() {
    return <RelationshipPopoverList options={this.state.popoverOptions} onClick={this.menuOptionClick} />;
  }

  onItemClick(itemId, modifiers = {}) {
    const processMultiSelection = (condition = false) => {
      updateSelectedItemsUsingUpdaters(
        condition,
        itemId,
        this.props.selectedItemIds,
        this.props.onRemoveFromSelection,
        this.props.onAddToSelection,
        this.props.onSelectItems
      );
    };
    //#!if iOS === false
    if (modifiers.SHIFT && this._lastClickItemId) {
      // If this is the first shift click since some other click, record the current selection
      // as the starting point for any subsequent shift clicks
      if (!this._firstShiftClickSelection) {
        this._firstShiftClickSelection = this.props.selectedItemIds;
      }
      // Select everything from last click to current position
      const a = this.props.sortedItemIds.indexOf(this._lastClickItemId);
      const b = this.props.sortedItemIds.indexOf(itemId);
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      const newIds = this.props.sortedItemIds.slice(low, high + 1).toArray();
      // Note that we can't simply add to selection, as we may want to select a lesser amount than was
      // selected with the previous shift click.
      // So instead we add this new selection to the original selection before the shift clicks began
      const allIds = this._firstShiftClickSelection.push(...newIds).toArray();
      this.props.onSelectItems(allIds);
      return;
    }

    // If we have a non-shift click, record it so we can use it as the starting point for any subsequent
    // shift clicks
    this._lastClickItemId = itemId;
    // Erase the first shift click so we start again the next time we do a shift click
    this._firstShiftClickSelection = null;

    // And then go on and handle non-shift clicks normally!
    processMultiSelection(modifiers.CONTROL || modifiers.COMMAND);
    //#!elseif iOS === true
// processMultiSelection(this.props.screenContext.isEditMode);
    //#!endif
  }

  onLiveRowDrag(itemIds, dropRow, dropAction) {
    if (!this.props.allowRowDragNesting) {
      this.props.onLiveRowDrag(itemIds, dropRow, dropAction);
    } else {
      this.setState({
        dropRow,
        dropAction
      });
    }
  }

  onDragEnter(_itemIds) {
    // this.props.onStartRowDrag(itemIds);
  }

  onDragLeave() {
    if (!this.props.allowRowDragNesting) {
      this.props.onDragLeave();
    } else {
      this.setState({
        dropRow: null,
        dropAction: null
      });
    }
  }

  onRowDrop(dragItem, dropRow, dropAction) {
    // Send the drop info to owner
    this.props.onRowDrop(dragItem, dropRow, dropAction);

    if (this.props.allowRowDragNesting) {
      // And if necessary, clear our drawn drop lines...
      this.setState({
        dropRow: null,
        dropAction: null
      });
    }
  }

  renderMainViews() {
    const childComponents = [
      <RelationshipCardCanvasView
        key="cardCanvasView"
        {...this.props}
        onItemClick={this.onItemClick}
        onToggleChildren={this.props.onToggleChildren}
        drawDropRow={this.state.dropRow}
        drawDropAction={this.state.dropAction}
        onLiveRowDrag={this.onLiveRowDrag}
        onDragEnter={this.onDragEnter}
        onDragLeave={this.onDragLeave}
        onRowDrop={this.onRowDrop}
        requestCanDropItems={this.props.requestCanDropItems}
        adjustItemEndWhenChangingStart={this.props.adjustItemEndWhenChangingStart}
        onCreateItem={this.props.onCreateItem}
        types={this.props.types}
        defaultType={this.props.defaultType}
        typesById={this.props.typesById}
      />,
      <RelationshipColumnCanvasView
        key="columnCanvasView"
        {...this.props}
        onIntersectionClick={this.showMenu}
        onRowClick={this.selectRow}
      />
    ];

    return <React.Fragment>{childComponents}</React.Fragment>;
  }

  onClickPopoverBackdrop(e) {
    e.stopPropagation();
    this.hideMenu();
  }

  render() {
    const result = (
      <div onClick={this.props.onViewClick} className="relationshipView" onPaste={this.props.onPaste}>
        {this.renderMainViews()}
        {wrapEditingComponent(
          <Fragment>
            {this.state.popoverOpen ? (
              <div className="relationshipPopoverBackdrop" onClick={this.onClickPopoverBackdrop} />
            ) : null}
            {this.state.popoverOpen ? (
              <FocusedPopover
                className="relationshipPopover"
                placement="bottom"
                hideArrow={false}
                isOpen={this.state.popoverOpen}
                target={this.state.popoverTargetId}
                toggle={this.hideMenu}
                trigger="legacy"
              >
                <PopoverBody onClick={e => e.stopPropagation()}>{this.renderMenu()}</PopoverBody>
              </FocusedPopover>
            ) : null}
          </Fragment>
        )}
      </div>
    );
    return result;
  }
}

RelationshipCanvasView.propTypes = {
  hasData: PropTypes.bool.isRequired,
  viewId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  data: PropTypeShapes.relationshipData.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  allowedReferences: PropTypeShapes.allowedReferencePerSubjectObjectMap.isRequired,
  allowedInverseReferences: PropTypeShapes.allowedReferencePerSubjectObjectMap.isRequired,
  fontSize: PropTypes.number.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  onViewClick: PropTypes.func.isRequired,
  onRelationshipChange: PropTypes.func.isRequired,
  relationshipViewSettings: PropTypeShapes.relationshipViewSettings.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,

  typesById: ImmutablePropTypes.map.isRequired,

  onPaste: PropTypes.func,

  allowRowDrag: PropTypes.bool,
  allowRowDragNesting: PropTypes.bool,
  draggedRowIds: ImmutablePropTypes.list,
  canDrop: PropTypes.bool.isRequired,
  canItemDropOnRow: PropTypes.func.isRequired,
  requestCanDropItems: PropTypes.func.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,

  // Dragging rows
  onDragLeave: PropTypes.func,
  onStartRowDrag: PropTypes.func,
  onLiveRowDrag: PropTypes.func,
  onRowDrop: PropTypes.func,

  canTypeHaveSuperType: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  types: ImmutablePropTypes.list.isRequired,
  defaultType: PropTypes.string.isRequired,
  defaultTypeLabel: PropTypes.string.isRequired,
  isGlobalFilterActive: PropTypes.bool,
  isViewFilterActive: PropTypes.bool,
  onShowInspector: PropTypes.func,

  // From withContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

// export default RelationshipCanvasView;
const scrollableViewOptions = {
  scrollHorizontally: false,
  scrollVertically: true,
  showHorizontalScrollbar: false,
  showVerticalScrollbar: true
};

const normalView = withContainerContext(
  withScrollableView(withScrollPositionContext(RelationshipCanvasView), scrollableViewOptions)
);
let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
